/**
 * Binary PCM frame format (architecture §5.5, D3).
 *
 * The current APK sends audio by building `new Float32Array([0.001,-0.002,...])`
 * as JavaScript *source text* and evaluating it — roughly 48 KB of script parsed
 * on the main thread for every 8 KB of audio, about twelve times a second. This
 * format replaces that with a plain binary buffer.
 *
 *   offset  size  field
 *   0       4     magic       'TPCM', so a stray message is rejected not decoded
 *   4       4     seq         wraps at 2^32; gaps make drops observable
 *   8       4     sampleRate  declared per frame, not queried separately
 *   12      2     frameCount  samples in this frame
 *   14      1     sourceId    which capture device produced it
 *   15      1     reserved    keeps the PCM payload 2-byte aligned
 *   16      ...   int16 PCM, little-endian
 *
 * Carrying the sample rate in every frame removes the first-chunk race: there is
 * no window in which audio is flowing but the rate is still unknown, and a
 * mid-stream rate change (a real UAC renegotiation) announces itself instead of
 * silently shifting pitch.
 */

export const FRAME_MAGIC = 0x5450434d; // 'TPCM'
export const FRAME_HEADER_BYTES = 16;

export const SOURCE_STETHOSCOPE = 1;
/** Translated voice from the translation server's downlink (§8.9). */
export const SOURCE_TRANSLATED_VOICE = 2;

export interface PcmFrame {
  seq: number;
  sampleRate: number;
  frameCount: number;
  sourceId: number;
  /** Normalised to [-1, 1]. */
  samples: Float32Array;
}

export type FrameDecodeResult =
  | { ok: true; frame: PcmFrame }
  | { ok: false; reason: string };

/**
 * Decode one frame.
 *
 * Every failure is reported rather than swallowed. A malformed frame that
 * decodes to an empty array is indistinguishable from silence, and silence is
 * exactly what a broken stethoscope produces — so "returns nothing" must never
 * be a valid outcome of a parse error.
 */
export function decodeFrame(buffer: ArrayBuffer): FrameDecodeResult {
  if (buffer.byteLength < FRAME_HEADER_BYTES) {
    return {
      ok: false,
      reason: `frame too short: ${buffer.byteLength} bytes, need at least ${FRAME_HEADER_BYTES}`,
    };
  }

  const view = new DataView(buffer);
  const magic = view.getUint32(0, false);
  if (magic !== FRAME_MAGIC) {
    return {
      ok: false,
      reason: `bad magic 0x${magic.toString(16)} — not a PCM frame`,
    };
  }

  const seq = view.getUint32(4, true);
  const sampleRate = view.getUint32(8, true);
  const frameCount = view.getUint16(12, true);
  const sourceId = view.getUint8(14);

  const expectedBytes = FRAME_HEADER_BYTES + frameCount * 2;
  if (buffer.byteLength < expectedBytes) {
    return {
      ok: false,
      reason: `truncated: header declares ${frameCount} samples (${expectedBytes} bytes) but frame is ${buffer.byteLength}`,
    };
  }

  if (sampleRate <= 0) {
    return { ok: false, reason: `invalid sample rate ${sampleRate}` };
  }

  const samples = new Float32Array(frameCount);
  for (let i = 0; i < frameCount; i++) {
    // int16 → [-1, 1). Divisor is 32768 so full-scale negative maps to exactly
    // -1.0 and the conversion is a pure scale with no rounding asymmetry.
    samples[i] = view.getInt16(FRAME_HEADER_BYTES + i * 2, true) / 32768;
  }

  return {
    ok: true,
    frame: { seq, sampleRate, frameCount, sourceId, samples },
  };
}

/** Encode a frame. Used by tests and the mock bridge. */
export function encodeFrame(
  seq: number,
  sampleRate: number,
  samples: Float32Array,
  sourceId: number = SOURCE_STETHOSCOPE,
): ArrayBuffer {
  const buffer = new ArrayBuffer(FRAME_HEADER_BYTES + samples.length * 2);
  const view = new DataView(buffer);

  view.setUint32(0, FRAME_MAGIC, false);
  view.setUint32(4, seq >>> 0, true);
  view.setUint32(8, sampleRate, true);
  view.setUint16(12, samples.length, true);
  view.setUint8(14, sourceId);
  view.setUint8(15, 0);

  for (let i = 0; i < samples.length; i++) {
    // Clamp before scaling: a sample above full scale would otherwise wrap to
    // the opposite sign, turning a loud peak into a discontinuity.
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(
      FRAME_HEADER_BYTES + i * 2,
      Math.round(clamped * 32767),
      true,
    );
  }

  return buffer;
}

/** Sequence gap between two frames, accounting for uint32 wrap. */
export function sequenceGap(previousSeq: number, currentSeq: number): number {
  return ((currentSeq - previousSeq - 1) >>> 0) & 0xffffffff;
}
